import { codeUnitString } from "../code-unit-string.js";
import { z } from "zod";
//#region src/kernel/kernel-wire-internal.ts
/**
* Grammars that the WS-1b kernel wire modules share (kernel apps, WS-1b, R-042). Internal to
* `@zion/sdk-contracts`: the root barrel does not re-export this module, and the package `exports`
* map gives no path to it. A consumer imports the public names of `step-up.ts`,
* `second-factor.ts`, `kernel-handoff.ts` and `authentication.ts`.
*
* Browser-safe: no Node builtin and no Node global. Every pattern here is linear: each repetition
* has a fixed separator or a bounded length, and each schema puts `.max()` before `.regex()` (the
* ReDoS rule of `id-grammar.ts`). No schema uses `.transform()`.
*/
/**
* The code points that a text shown to a person must not hold: controls (`Cc`), format characters
* (`Cf`, the bidi controls among them), lone surrogates (`Cs`), and the line and paragraph
* separators (`Zl`, `Zp`). With the `u` flag `\p{Cs}` matches only an unpaired surrogate.
*/
const REFUSED_DISPLAY_CODE_POINT = /[\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/u;
/** A text with no {@link REFUSED_DISPLAY_CODE_POINT}, in the form a JSON Schema `pattern` holds. */
const DISPLAY_TEXT_PATTERN = /^[^\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]*$/u;
/** A display text of `min` to `max` UTF-16 code units with no refused code point. */
function displayTextSchema(min, max) {
	return codeUnitString().min(min).max(max).regex(DISPLAY_TEXT_PATTERN, "the text holds a control, format, surrogate or separator code point");
}
/**
* A lower-case DNS host name: 1 to 253 characters, labels of 1 to 63 `[a-z0-9-]` characters that
* start and end with a letter or digit, joined by full stops. No port, no scheme, no trailing dot.
*/
const LOWER_HOST_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/;
/** The zod form of {@link LOWER_HOST_PATTERN}, at most 253 characters. */
const lowerHostSchema = z.string().min(1).max(253).regex(LOWER_HOST_PATTERN, "a lower-case host name with no port");
/** A non-empty base64url text with no padding, of 1 to `max` characters. */
function base64UrlTextSchema(max) {
	return z.string().min(1).max(max).regex(/^[A-Za-z0-9_-]+$/, "base64url text with no padding");
}
/** The 43-character base64url text of 32 bytes: a WebAuthn challenge, a PKCE challenge, a handoff code. */
const BASE64URL_32_BYTES_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** The zod form of {@link BASE64URL_32_BYTES_PATTERN}. */
const base64Url32BytesSchema = z.string().length(43).regex(BASE64URL_32_BYTES_PATTERN, "43 base64url characters");
/** An ISO 8601 UTC timestamp, the `Date.prototype.toISOString` form. */
const isoTimestampSchema = z.iso.datetime();
/**
* The text of a step-up page URL: `https://`, a lower-case host with an optional port, and the path
* `/step-up/<lower-case uuid>`. No user name, password, query or fragment. At most 2,048 characters.
*/
const STEP_UP_URL_PATTERN = /^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*(?::[0-9]{1,5})?\/step-up\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** The zod form of {@link STEP_UP_URL_PATTERN}. */
const stepUpUrlSchema = z.string().max(2048).regex(STEP_UP_URL_PATTERN, "an https step-up page URL");
/** True when `url` ends in the step-up page path of `requestId`. Pure. */
function isStepUpUrlOf(url, requestId) {
	return url.endsWith(`/step-up/${requestId}`);
}
//#endregion
export { BASE64URL_32_BYTES_PATTERN, DISPLAY_TEXT_PATTERN, LOWER_HOST_PATTERN, REFUSED_DISPLAY_CODE_POINT, STEP_UP_URL_PATTERN, base64Url32BytesSchema, base64UrlTextSchema, displayTextSchema, isStepUpUrlOf, isoTimestampSchema, lowerHostSchema, stepUpUrlSchema };

//# sourceMappingURL=kernel-wire-internal.js.map